import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from "antd";
import {
  CheckCircleFilled,
  CrownFilled,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  LockOutlined,
  PlusOutlined,
  SafetyOutlined,
  SearchOutlined,
  TeamOutlined,
  UnlockOutlined,
  UserOutlined
} from "@ant-design/icons";
import { transportApi } from "../../api/transportApi";
import { ADMIN_ROLES } from "../../shared/utils/permissions";

const { Title, Text, Paragraph } = Typography;

// ─── Types ────────────────────────────────────────────────────────────────────
interface User {
  id: string;
  email: string;
  fullName: string;
  branchId?: number | null;
  roles: string[];
  isLocked: boolean;
}

interface PermissionState {
  [module: string]: { [action: string]: boolean };
}

// ─── Permission helpers ───────────────────────────────────────────────────────
const PERMISSION_MODULES = ["Transport", "Fleet", "Consignments", "Accounting", "Parties", "InventoryIssue", "Users"];
const PERMISSION_ACTIONS = ["Read", "Create", "Update", "Delete"];

const MODULE_DESCRIPTIONS: Record<string, string> = {
  Transport: "Manifests, Trips, E-Way Bills, Operations, Reports",
  Fleet: "Vehicles, Drivers, Lorry Hire",
  Consignments: "Consignment Booking & Management",
  Accounting: "Finance, Accounting, Commission",
  Parties: "Transport Customer, Consignor, Consignee & Supplier Registry",
  InventoryIssue: "Issue and manage Bilty / LR, Manifest and Gate Pass stationery ranges",
  Users: "User Management (Admin only)"
};

const ACTION_DESCRIPTIONS: Record<string, string> = {
  Read: "View records",
  Create: "Add new records",
  Update: "Edit existing records",
  Delete: "Remove records"
};

const ACTION_COLORS: Record<string, string> = {
  Read: "blue",
  Create: "green",
  Update: "orange",
  Delete: "red"
};

function getImpliedActions(action: string): string[] {
  switch (action) {
    case "Delete": return ["Delete", "Update", "Create", "Read"];
    case "Update": return ["Update", "Create", "Read"];
    case "Create": return ["Create", "Read"];
    case "Read":   return ["Read"];
    default:       return [];
  }
}

function getDependentActions(action: string): string[] {
  switch (action) {
    case "Read":   return ["Read", "Create", "Update", "Delete"];
    case "Create": return ["Create", "Update", "Delete"];
    case "Update": return ["Update", "Delete"];
    case "Delete": return ["Delete"];
    default:       return [];
  }
}

function emptyPermState(): PermissionState {
  const s: PermissionState = {};
  PERMISSION_MODULES.forEach((m) => {
    s[m] = {};
    PERMISSION_ACTIONS.forEach((a) => { s[m][a] = false; });
  });
  return s;
}

function minimumEmployeePermState(): PermissionState {
  const state = emptyPermState();
  ["Read", "Create", "Update"].forEach((action) => { state.Transport[action] = true; });
  state.Fleet.Read = true;
  ["Read", "Create", "Update"].forEach((action) => { state.Consignments[action] = true; });
  ["Read", "Create"].forEach((action) => { state.Parties[action] = true; });
  state.InventoryIssue.Read = true;
  return state;
}

function permissionsToState(permissions: string[]): PermissionState {
  const state = emptyPermState();
  PERMISSION_MODULES.forEach((m) => {
    PERMISSION_ACTIONS.forEach((a) => {
      state[m][a] = permissions.includes(`${m}.${a}`);
    });
  });
  return state;
}

function stateToPermissions(state: PermissionState): string[] {
  const out: string[] = [];
  Object.keys(state).forEach((m) => {
    Object.keys(state[m]).forEach((a) => {
      if (state[m][a]) out.push(`${m}.${a}`);
    });
  });
  return out;
}

function applyPermissionCascade(
  state: PermissionState, module: string, action: string, checked: boolean
): PermissionState {
  const next = JSON.parse(JSON.stringify(state)) as PermissionState;
  if (checked) getImpliedActions(action).forEach((a) => { next[module][a] = true; });
  else         getDependentActions(action).forEach((a) => { next[module][a] = false; });
  return next;
}

function isUserAdmin(roles: string[]): boolean {
  return roles.some((r) => ADMIN_ROLES.includes(r.toLowerCase()));
}

function grantedCount(state: PermissionState): number {
  return Object.values(state).flatMap(Object.values).filter(Boolean).length;
}

// ─── Permission Matrix ────────────────────────────────────────────────────────
interface PermissionMatrixProps {
  state: PermissionState;
  onChange: (next: PermissionState) => void;
  loading?: boolean;
}

const PermissionMatrix: React.FC<PermissionMatrixProps> = ({ state, onChange, loading }) => {
  const count = grantedCount(state);
  const changePermission = (module: string, action: string, checked: boolean) => {
    if (module === "InventoryIssue" && checked) {
      message.info("Inventory issue access enabled. This user can work with controlled document-number stationery according to the selected action level.");
    }
    onChange(applyPermissionCascade(state, module, action, checked));
  };

  return (
    <Spin spinning={!!loading}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`${count} permission${count !== 1 ? "s" : ""} granted`}
        description="New employees start with minimum operational access for transport work, fleet viewing, consignments, party lookup/creation and stationery viewing. Accounting, deletion and user administration remain off until explicitly granted."
      />
      {PERMISSION_MODULES.map((module) => (
        <div key={module} style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
            <Text strong style={{ fontSize: 13 }}>{module}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>{MODULE_DESCRIPTIONS[module]}</Text>
          </div>
          <Row gutter={[8, 8]}>
            {PERMISSION_ACTIONS.map((action) => {
              const checked = state[module]?.[action] ?? false;
              return (
                <Col key={action} span={6}>
                  <div
                    style={{
                      padding: "7px 10px",
                      borderRadius: 6,
                      border: `1px solid ${checked ? "#1677ff" : "var(--app-bg, #f0f0f0)"}`,
                      background: checked ? "rgba(22,119,255,0.06)" : "transparent",
                      cursor: "pointer",
                      transition: "all 0.15s"
                    }}
                    onClick={() => changePermission(module, action, !checked)}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={(e) => changePermission(module, action, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span style={{ fontWeight: checked ? 600 : 400, fontSize: 12 }}>{action}</span>
                    </Checkbox>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 2, paddingLeft: 22 }}>
                      {ACTION_DESCRIPTIONS[action]}
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
          <Divider style={{ margin: "10px 0 0" }} />
        </div>
      ))}
    </Spin>
  );
};

// ─── Admin Full Access Badge ──────────────────────────────────────────────────
const AdminAccessBadge = ({ roles }: { roles: string[] }) => {
  const adminRole = roles.find((r) => ADMIN_ROLES.includes(r.toLowerCase())) ?? "Admin";
  return (
    <Tooltip placement="bottom"
      title={
        <div style={{ maxWidth: 260 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            <CrownFilled style={{ color: "#faad14", marginRight: 6 }} />
            Full System Access
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
            The <strong>{adminRole}</strong> role grants unrestricted access to all modules. No individual
            permission configuration is needed.
          </div>
        </div>
      }
      color="rgba(0,0,0,0.85)"
    >
      <Tag
        icon={<CheckCircleFilled />}
        color="success"
        style={{ cursor: "default", fontWeight: 600, padding: "3px 10px", fontSize: 12 }}
      >
        Full System Access
      </Tag>
    </Tooltip>
  );
};

// ─── User Drawer (Add / Edit) ─────────────────────────────────────────────────
interface UserDrawerProps {
  open: boolean;
  editingUser: User | null;
  availableRoles: string[];
  onClose: () => void;
  onSaved: () => void;
}

const UserDrawer: React.FC<UserDrawerProps> = ({ open, editingUser, availableRoles, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [permState, setPermState] = useState<PermissionState>(emptyPermState());
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [branchOptions, setBranchOptions] = useState<{ value: number; label: string }[]>([]);

  const isEditing = !!editingUser;
  const selectedIsAdmin = isUserAdmin(selectedRoles);

  useEffect(() => {
    if (!open) return;

    if (editingUser) {
      const parts = (editingUser.fullName || "").trim().split(/\s+/);
      const firstName = parts[0] ?? "";
      const lastName = parts.slice(1).join(" ");
      form.setFieldsValue({ email: editingUser.email, firstName, lastName, branchId: editingUser.branchId ?? undefined });
      setSelectedRoles(editingUser.roles);

      if (!isUserAdmin(editingUser.roles)) {
        setLoadingPerms(true);
        transportApi.getUserPermissions(editingUser.id)
          .then((r) => setPermState(permissionsToState(r.permissions ?? [])))
          .catch(() => message.error("Failed to load permissions"))
          .finally(() => setLoadingPerms(false));
      } else {
        setPermState(emptyPermState());
      }
    } else {
      form.resetFields();
      setSelectedRoles([]);
      setPermState(minimumEmployeePermState());
    }
  }, [open, editingUser]);

  useEffect(() => {
    if (!open) return;
    transportApi.searchBranchLookups("", 1, 200)
      .then(result => setBranchOptions(result.items.map(x => ({ value: x.id, label: x.label }))))
      .catch(() => message.error("Failed to load branches"));
  }, [open]);

  // When roles change to admin, clear perm state; when going back to non-admin, keep whatever was set
  useEffect(() => {
    if (selectedIsAdmin) setPermState(emptyPermState());
  }, [selectedIsAdmin]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (isEditing && editingUser) {
        await transportApi.updateAdminUser(editingUser.id, {
          email: values.email,
          firstName: values.firstName ?? "",
          lastName: values.lastName ?? "",
          branchId: selectedIsAdmin ? undefined : values.branchId,
          roles: selectedRoles
        });
        if (!selectedIsAdmin) {
          await transportApi.setUserPermissions(editingUser.id, stateToPermissions(permState));
          if (Object.values(permState.InventoryIssue ?? {}).some(Boolean)) message.success("Inventory issue permissions are active for this user.");
        }
        message.success("User updated successfully");
      } else {
        const created = await transportApi.createAdminUser({
          email: values.email,
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
          branchId: selectedIsAdmin ? undefined : values.branchId,
          roles: selectedRoles,
          permissions: selectedIsAdmin ? [] : stateToPermissions(permState)
        });
        if (!selectedIsAdmin && created?.id && Object.values(permState.InventoryIssue ?? {}).some(Boolean)) message.success("Inventory issue permissions are active for this user.");
        message.success("User created successfully");
      }

      onSaved();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.errors?.join(", ");
      if (msg) message.error(msg);
      // Form validation errors are shown inline — no extra message needed
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={
        <Space size={10}>
          <UserOutlined style={{ color: "#1677ff" }} />
          <div>
            <div style={{ fontWeight: 700 }}>{isEditing ? "Edit User" : "Add New User"}</div>
            {isEditing && (
              <div style={{ fontSize: 12, fontWeight: 400, color: "#888" }}>
                {editingUser?.fullName || editingUser?.email}
              </div>
            )}
          </div>
        </Space>
      }
      open={open}
      onClose={onClose}
      width={860}
      destroyOnClose
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            {isEditing ? "Save Changes" : "Create User"}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        {/* ── Account Details ── */}
        <Title level={5} style={{ marginBottom: 12 }}>
          <UserOutlined style={{ marginRight: 8, color: "#1677ff" }} />
          Account Details
        </Title>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="firstName" label="First Name">
              <Input placeholder="First name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="lastName" label="Last Name">
              <Input placeholder="Last name" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="email"
          label="Email Address"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Enter a valid email address" }
          ]}
        >
          <Input prefix={<UserOutlined />} placeholder="user@example.com" />
        </Form.Item>

        <Form.Item
          name="branchId"
          label="Assigned Branch"
          rules={[{
            required: !selectedIsAdmin,
            message: "Select the branch this user will operate from"
          }]}
          extra={selectedIsAdmin ? "Optional for administrators with cross-branch access." : "This user can create operational records only for this branch."}
        >
          <Select
            allowClear={selectedIsAdmin}
            showSearch
            optionFilterProp="label"
            placeholder="Select branch"
            options={branchOptions}
          />
        </Form.Item>

        {!isEditing && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: "Password is required" },
                  { min: 8, message: "Minimum 8 characters" },
                  {
                    pattern: /^(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])/,
                    message: "Must include uppercase, number, and a special character"
                  }
                ]}
              >
                <Input.Password placeholder="Min. 8 characters" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Please confirm the password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) return Promise.resolve();
                      return Promise.reject(new Error("Passwords do not match"));
                    }
                  })
                ]}
              >
                <Input.Password placeholder="Repeat password" />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Divider style={{ margin: "8px 0 20px" }} />

        {/* ── Role Assignment ── */}
        <Title level={5} style={{ marginBottom: 4 }}>
          <CrownFilled style={{ marginRight: 8, color: "#faad14" }} />
          Role Assignment
        </Title>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          Admin roles grant unrestricted access to the entire system — no further permission setup is needed.
        </Paragraph>

        {availableRoles.length === 0 ? (
          <Alert type="warning" message="No roles found. Ask a system administrator to create roles first." showIcon />
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
            {availableRoles.map((role) => {
              const isAdmin = ADMIN_ROLES.includes(role.toLowerCase());
              const checked = selectedRoles.includes(role);
              return (
                <div
                  key={role}
                  onClick={() =>
                    setSelectedRoles((prev) =>
                      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
                    )
                  }
                  style={{
                    padding: "6px 14px",
                    borderRadius: 20,
                    border: `1px solid ${checked ? (isAdmin ? "#faad14" : "#1677ff") : "#d9d9d9"}`,
                    background: checked
                      ? isAdmin ? "rgba(250,173,20,0.1)" : "rgba(22,119,255,0.08)"
                      : "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    transition: "all 0.15s"
                  }}
                >
                  {isAdmin && <CrownFilled style={{ color: "#faad14", fontSize: 12 }} />}
                  <span style={{ fontWeight: checked ? 600 : 400, fontSize: 13 }}>{role}</span>
                  {checked && (
                    <CheckCircleFilled style={{ color: isAdmin ? "#faad14" : "#1677ff", fontSize: 12 }} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {selectedRoles.length > 0 && (
          <div style={{ marginTop: 10, marginBottom: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Selected: </Text>
            {selectedRoles.map((r) => (
              <Tag
                key={r}
                color={ADMIN_ROLES.includes(r.toLowerCase()) ? "gold" : "blue"}
                icon={ADMIN_ROLES.includes(r.toLowerCase()) ? <CrownFilled /> : undefined}
                style={{ marginRight: 4 }}
              >
                {r}
              </Tag>
            ))}
          </div>
        )}

        <Divider style={{ margin: "20px 0" }} />

        {/* ── Permissions ── */}
        <Title level={5} style={{ marginBottom: 4 }}>
          <SafetyOutlined style={{ marginRight: 8, color: "#1677ff" }} />
          Module Permissions
        </Title>

        {selectedIsAdmin ? (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleFilled />}
            message="Full system access granted via Admin role"
            description="Administrators automatically receive unrestricted access to all modules. Individual permissions are not required."
            style={{ marginBottom: 8 }}
          />
        ) : (
          <PermissionMatrix state={permState} onChange={setPermState} loading={loadingPerms} />
        )}
      </Form>
    </Drawer>
  );
};

// ─── Reset Password Modal ─────────────────────────────────────────────────────
interface ResetPasswordModalProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ open, user, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open]);

  const handleReset = async () => {
    try {
      const values = await form.validateFields();
      if (!user) return;
      setLoading(true);
      await transportApi.resetUserPassword(user.id, values.newPassword);
      message.success(`Password reset for ${user.fullName || user.email}`);
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.errors?.join(", ");
      if (msg) message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space size={10}>
          <KeyOutlined style={{ color: "#1677ff" }} />
          <div>
            <div style={{ fontWeight: 700 }}>Reset Password</div>
            {user && (
              <div style={{ fontSize: 12, fontWeight: 400, color: "#888" }}>
                {user.fullName || user.email}
              </div>
            )}
          </div>
        </Space>
      }
      open={open}
      onCancel={onClose}
      onOk={handleReset}
      okText="Reset Password"
      okButtonProps={{ loading, danger: false }}
      destroyOnHidden
      width={440}
    >
      <Alert
        type="warning"
        showIcon
        message="The user will need to use this new password to log in."
        style={{ marginBottom: 20, marginTop: 8 }}
      />
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          name="newPassword"
          label="New Password"
          rules={[
            { required: true, message: "Please enter a new password" },
            { min: 8, message: "Minimum 8 characters" },
            {
              pattern: /^(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])/,
              message: "Must include uppercase, number, and a special character"
            }
          ]}
        >
          <Input.Password placeholder="Min. 8 characters" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirm New Password"
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: "Please confirm the password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) return Promise.resolve();
                return Promise.reject(new Error("Passwords do not match"));
              }
            })
          ]}
        >
          <Input.Password placeholder="Repeat new password" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─── Roles Overview Tab ───────────────────────────────────────────────────────
interface RolesTabProps {
  users: User[];
  availableRoles: string[];
  loading: boolean;
}

const RolesTab: React.FC<RolesTabProps> = ({ users, availableRoles, loading }) => {
  const roleStats = useMemo(() => {
    const stats: Record<string, User[]> = {};
    availableRoles.forEach((r) => { stats[r] = []; });
    users.forEach((u) => {
      u.roles.forEach((role) => {
        if (!stats[role]) stats[role] = [];
        stats[role].push(u);
      });
    });
    return stats;
  }, [users, availableRoles]);

  const columns = [
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 180,
      render: (role: string) => {
        const isAdmin = ADMIN_ROLES.includes(role.toLowerCase());
        return (
          <Space>
            <Tag
              color={isAdmin ? "gold" : "blue"}
              icon={isAdmin ? <CrownFilled /> : <TeamOutlined />}
              style={{ fontWeight: 600, fontSize: 13, padding: "3px 10px" }}
            >
              {role}
            </Tag>
            {isAdmin && (
              <Tooltip placement="bottom" title="Admin roles grant full system access">
                <Tag color="green" style={{ fontSize: 11 }}>Full Access</Tag>
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: "Users",
      key: "count",
      width: 100,
      render: (_: any, record: { role: string }) => {
        const count = roleStats[record.role]?.length ?? 0;
        return (
          <Badge
            count={count}
            showZero
            style={{ backgroundColor: count > 0 ? "#1677ff" : "#d9d9d9", fontWeight: 600 }}
          />
        );
      }
    },
    {
      title: "Members",
      key: "members",
      render: (_: any, record: { role: string }) => {
        const members = roleStats[record.role] ?? [];
        if (members.length === 0) {
          return <Text type="secondary" style={{ fontSize: 12 }}>No users assigned</Text>;
        }
        const visible = members.slice(0, 8);
        const extra = members.length - 8;
        return (
          <Space size={4} wrap>
            {visible.map((u) => (
              <Tooltip placement="bottom" key={u.id} title={u.email}>
                <Tag style={{ fontSize: 12, margin: 2 }}>
                  {u.fullName || u.email.split("@")[0]}
                </Tag>
              </Tooltip>
            ))}
            {extra > 0 && (
              <Tooltip placement="bottom" title={members.slice(8).map((u) => u.fullName || u.email).join(", ")}>
                <Tag style={{ fontSize: 12, margin: 2, cursor: "pointer" }}>+{extra} more</Tag>
              </Tooltip>
            )}
          </Space>
        );
      }
    }
  ];

  const dataSource = availableRoles.map((role) => ({ role, key: role }));

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Roles are view-only here."
        description="This panel shows how many users are assigned to each role. To create new roles, use the system administration tools (Identity management). To assign roles to a user, use the Edit button on the Users tab."
      />
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowKey="role"
        size="small"
        pagination={false}
      />
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [activeTab, setActiveTab] = useState("users");

  // Add/Edit drawer
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Reset password modal
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [resetPwUser, setResetPwUser] = useState<User | null>(null);

  // Quick-permissions modal (for non-admin users from the table)
  const [permModalOpen, setPermModalOpen] = useState(false);
  const [permModalUser, setPermModalUser] = useState<User | null>(null);
  const [permModalState, setPermModalState] = useState<PermissionState>(emptyPermState());
  const [permModalLoading, setPermModalLoading] = useState(false);
  const [permSaving, setPermSaving] = useState(false);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      transportApi.listAdminUsers({ page: 1, pageSize: 200 }),
      transportApi.listRoles()
    ])
      .then(([usersData, rolesData]) => {
        setUsers(usersData);
        setAvailableRoles(rolesData);
        setPage(1);
      })
      .catch(() => message.error("Failed to load data"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const filteredUsers = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) =>
      u.email.toLowerCase().includes(term) ||
      (u.fullName ?? "").toLowerCase().includes(term) ||
      u.roles.some((r) => r.toLowerCase().includes(term))
    );
  }, [users, searchQuery]);

  const openAdd = () => {
    setEditingUser(null);
    setUserDrawerOpen(true);
  };

  const openEdit = (user: User) => {
    setEditingUser(user);
    setUserDrawerOpen(true);
  };

  const openResetPw = (user: User) => {
    setResetPwUser(user);
    setResetPwOpen(true);
  };

  const openPermissions = (user: User) => {
    setPermModalUser(user);
    setPermModalLoading(true);
    setPermModalOpen(true);
    transportApi.getUserPermissions(user.id)
      .then((r) => setPermModalState(permissionsToState(r.permissions ?? [])))
      .catch(() => message.error("Failed to load permissions"))
      .finally(() => setPermModalLoading(false));
  };

  const savePermissions = async () => {
    if (!permModalUser) return;
    setPermSaving(true);
    try {
      await transportApi.setUserPermissions(permModalUser.id, stateToPermissions(permModalState));
      message.success("Permissions updated");
      if (Object.values(permModalState.InventoryIssue ?? {}).some(Boolean)) message.success("Inventory issue permissions are active for this user.");
      setPermModalOpen(false);
      fetchAll();
    } catch {
      message.error("Failed to update permissions");
    } finally {
      setPermSaving(false);
    }
  };

  const confirmAction = (title: string, content: string, action: () => Promise<void>, okText: string, danger = false) => {
    Modal.confirm({
      title, content, okText, cancelText: "Cancel",
      okType: danger ? "danger" : "primary",
      onOk: () => action()
    });
  };

  // ─── Users table columns ───────────────────────────────────────────────────
  const columns: any[] = [
    {
      title: "#",
      width: 52,
      render: (_: any, __: User, index: number) => (page - 1) * pageSize + index + 1
    },
    {
      title: "User",
      key: "user",
      render: (_: any, record: User) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.fullName || record.email}</div>
          {record.fullName && (
            <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
          )}
        </div>
      )
    },
    {
      title: "Roles",
      dataIndex: "roles",
      key: "roles",
      width: 200,
      render: (roles: string[]) => (
        <Space size={4} wrap>
          {roles?.length
            ? roles.map((role) => (
                <Tag
                  key={role}
                  color={ADMIN_ROLES.includes(role.toLowerCase()) ? "gold" : "blue"}
                  icon={ADMIN_ROLES.includes(role.toLowerCase()) ? <CrownFilled /> : undefined}
                >
                  {role}
                </Tag>
              ))
            : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
          }
        </Space>
      )
    },
    {
      title: "Branch",
      dataIndex: "branchId",
      width: 110,
      render: (branchId: number | null | undefined) =>
        branchId ? <Tag>Branch #{branchId}</Tag> : <Text type="secondary">All branches</Text>
    },
    {
      title: "Status",
      dataIndex: "isLocked",
      width: 80,
      render: (isLocked: boolean) => (
        <Tag color={isLocked ? "red" : "green"} style={{ margin: 0 }}>
          {isLocked ? "Locked" : "Active"}
        </Tag>
      )
    },
    {
      title: "Access",
      key: "permissions",
      width: 160,
      render: (_: any, record: User) =>
        isUserAdmin(record.roles) ? (
          <AdminAccessBadge roles={record.roles} />
        ) : (
          <Tooltip placement="bottom" title="Configure module-level access">
            <Button size="small" icon={<SafetyOutlined />} onClick={() => openPermissions(record)}>
              Permissions
            </Button>
          </Tooltip>
        )
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_: any, record: User) => (
        <Space size={4} style={{ flexWrap: "nowrap" }}>
          <Tooltip placement="bottom" title="Edit user details and roles">
            <Button size="small" type="default" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>

          <Tooltip placement="bottom" title="Reset password">
            <Button size="small" type="default" icon={<KeyOutlined />} onClick={() => openResetPw(record)} />
          </Tooltip>

          {record.isLocked ? (
            <Tooltip placement="bottom" title="Unlock — allow user to log in">
              <Button
                size="small"
                type="default"
                icon={<UnlockOutlined />}
                onClick={() =>
                  confirmAction(
                    "Unlock User", `Unlock ${record.email}?`,
                    async () => { await transportApi.unlockUser(record.id); message.success("User unlocked"); fetchAll(); },
                    "Unlock"
                  )
                }
              />
            </Tooltip>
          ) : (
            <Tooltip placement="bottom" title="Lock — prevent user from logging in">
              <Button
                size="small"
                type="default"
                icon={<LockOutlined />}
                onClick={() =>
                  confirmAction(
                    "Lock User", `Lock ${record.email}? They will be unable to log in.`,
                    async () => { await transportApi.lockUser(record.id); message.success("User locked"); fetchAll(); },
                    "Lock"
                  )
                }
              />
            </Tooltip>
          )}

          {isUserAdmin(record.roles) ? (
            <Tooltip placement="bottom" title="Admin users are protected from deletion">
              <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => message.warning("Admin role users cannot be deleted here. Ask the Web Administrator explicitly to perform this protected operation.")} />
            </Tooltip>
          ) : <Popconfirm
            title="Delete user"
            description={`Permanently delete ${record.email}? This cannot be undone.`}
            onConfirm={async () => {
              await transportApi.deleteUser(record.id);
              message.success("User deleted");
              fetchAll();
            }}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Tooltip placement="bottom" title="Delete user permanently">
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>}
        </Space>
      )
    }
  ];

  // ─── Tab items ─────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: "users",
      label: (
        <span>
          <UserOutlined />
          Users
          <Badge
            count={users.length}
            showZero
            style={{ marginLeft: 6, backgroundColor: "#1677ff", fontSize: 10 }}
          />
        </span>
      ),
      children: (
        <>
          <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }} wrap>
            <Space>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search by email, name or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
                style={{ width: 300 }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {filteredUsers.length} of {users.length} users
              </Text>
            </Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
              Add User
            </Button>
          </Space>

          <Table
            columns={columns}
            dataSource={filteredUsers}
            loading={loading}
            rowKey="id"
            size="small"
            pagination={{
              current: page,
              pageSize,
              total: filteredUsers.length,
              showSizeChanger: true,
              pageSizeOptions: ["25", "50", "100"],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
              onChange: (p, ps) => { setPage(p); setPageSize(ps); }
            }}
          />
        </>
      )
    },
    {
      key: "roles",
      label: (
        <span>
          <TeamOutlined />
          Roles
          <Badge
            count={availableRoles.length}
            showZero
            style={{ marginLeft: 6, backgroundColor: "#722ed1", fontSize: 10 }}
          />
        </span>
      ),
      children: <RolesTab users={users} availableRoles={availableRoles} loading={loading} />
    }
  ];

  return (
    <Card className="panel-card">
      <div className="panel-title-row" style={{ marginBottom: 12 }}>
        <Space>
          <Title level={3} style={{ margin: 0 }}>User Management</Title>
        </Space>
        <Tooltip placement="bottom" title="Admins have automatic full access. Use the Permissions button on non-admin users to control their module access. Use Edit to change user details and role assignments.">
          <Button type="text" icon={<InfoCircleOutlined />} />
        </Tooltip>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <span>
            Users with{" "}
            <Tag icon={<CrownFilled />} color="gold" style={{ margin: "0 4px" }}>Admin</Tag>
            roles receive <strong>unrestricted access</strong> automatically.
            Use <strong>Edit</strong> to manage roles and <strong>Permissions</strong> to configure module access for regular users.
          </span>
        }
      />

      <Card
        tabList={tabItems.map((t) => ({ key: t.key, tab: t.label }))}
        activeTabKey={activeTab}
        onTabChange={setActiveTab}
        bodyStyle={{ paddingTop: 16 }}
      >
        {tabItems.find((t) => t.key === activeTab)?.children}
      </Card>

      {/* ── Add/Edit User Drawer ── */}
      <UserDrawer
        open={userDrawerOpen}
        editingUser={editingUser}
        availableRoles={availableRoles}
        onClose={() => setUserDrawerOpen(false)}
        onSaved={() => { setUserDrawerOpen(false); fetchAll(); }}
      />

      {/* ── Reset Password Modal ── */}
      <ResetPasswordModal
        open={resetPwOpen}
        user={resetPwUser}
        onClose={() => setResetPwOpen(false)}
      />

      {/* ── Quick Permissions Modal ── */}
      <Modal
        title={
          <Space size={10}>
            <SafetyOutlined style={{ color: "#1677ff" }} />
            <div>
              <div style={{ fontWeight: 700 }}>Manage Permissions</div>
              {permModalUser && (
                <div style={{ fontSize: 12, fontWeight: 400, color: "#888" }}>
                  {permModalUser.fullName || permModalUser.email}
                </div>
              )}
            </div>
          </Space>
        }
        open={permModalOpen}
        onCancel={() => setPermModalOpen(false)}
        onOk={savePermissions}
        okText="Save Permissions"
        confirmLoading={permSaving}
        width={860}
        destroyOnHidden
      >
        <PermissionMatrix
          state={permModalState}
          onChange={setPermModalState}
          loading={permModalLoading}
        />
      </Modal>
    </Card>
  );
};

export default UserManagementPage;
